// ============================================================================
//  NarrativeScans.js — scans attached to a specific narrative.
// ----------------------------------------------------------------------------
//  Scans are the imaging subset of the NHIS investigation catalogue: X-ray,
//  Ultrasound, CT, MRI, Doppler, ECG, EEG, etc. The picker only lists those
//  INVE codes. The AI can (and should) return zero scans when imaging isn't
//  clinically indicated — most simple OPD cases don't need a scan.
// ============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  templateScans,
  templateNarrativeScans, templateNarrativeScanRow, templateNarrativeScansSuggest,
} from '../../BaseURL';
import notify from './notify';
import { IconPlus, IconSparkle, IconEdit, IconTrash, IconSearch, IconClose } from './icons';

export default function NarrativeScans({ narrativeId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [suggesting, setSuggesting] = useState(false);
  const [picking, setPicking] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(templateNarrativeScans(narrativeId));
      setRows(data.rows || []);
    } catch (err) {
      notify.error('Failed to load scans', err?.response?.data?.error || err.message);
    } finally { setLoading(false); }
  }, [narrativeId]);

  useEffect(() => { load(); }, [load]);

  const remove = async (row) => {
    const ok = await notify.confirm(
      'Remove this scan?',
      `${row.scan_code} · ${row.scan_name} will be detached from this narrative.`,
      { confirmText: 'Remove', cancelText: 'Cancel', danger: true }
    );
    if (!ok) return;
    try {
      await axios.delete(templateNarrativeScanRow(narrativeId, row.id));
      notify.toast.success('Removed');
      load();
    } catch (err) {
      notify.error('Delete failed', err?.response?.data?.error || err.message);
    }
  };

  const suggest = async () => {
    setSuggesting(true);
    try {
      const { data } = await axios.post(templateNarrativeScansSuggest(narrativeId), { count: 3 });
      if (data.inserted === 0) {
        notify.toast.info('AI decided no imaging is indicated for this narrative');
      } else {
        const msg = data.rejectedOutOfCatalogue
          ? `${data.inserted} added · ${data.rejectedOutOfCatalogue} rejected (outside catalogue)`
          : `${data.inserted} scan${data.inserted === 1 ? '' : 's'} added`;
        notify.toast.success(msg);
      }
      load();
    } catch (err) {
      notify.error('AI suggest failed', err?.response?.data?.error || err.message);
    } finally { setSuggesting(false); }
  };

  return (
    <div className="tp-narr-labs">
      <div className="tp-narr-labs-head">
        <div className="tp-narr-labs-title">
          <span className="tp-narr-section-label">Scans / Imaging</span>
          <span className="tp-hint">
            Only picked when clinically indicated — most simple OPD cases won't need one
          </span>
        </div>
        <div className="tp-narr-labs-actions">
          <button type="button" className="tp-btn tp-btn-ghost tp-btn-sm"
                  onClick={suggest} disabled={suggesting}>
            <IconSparkle size={14} />
            {suggesting ? 'Asking AI…' : 'Suggest scans with AI'}
          </button>
          <button type="button" className="tp-btn tp-btn-primary tp-btn-sm" onClick={() => setPicking(true)}>
            <IconPlus size={14} /> Add scan
          </button>
        </div>
      </div>

      {loading && <div className="tp-hint">Loading scans…</div>}

      {!loading && rows.length === 0 && (
        <div className="tp-narr-labs-empty">
          No scans attached. This is fine for most cases — imaging is only
          added when the presentation warrants it.
        </div>
      )}

      {!loading && rows.length > 0 && (
        <div className="tp-narr-labs-list">
          {rows.map((r) => (
            <ScanCard key={r.id} row={r} onEdit={() => setEditing(r)} onDelete={() => remove(r)} />
          ))}
        </div>
      )}

      {picking && (
        <ScanPicker
          narrativeId={narrativeId}
          onClose={() => setPicking(false)}
          onAdded={() => { setPicking(false); load(); }}
        />
      )}

      {editing && (
        <ScanEditor
          narrativeId={narrativeId}
          row={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

/* ── Single scan card ────────────────────────────────────────────────── */
function ScanCard({ row, onEdit, onDelete }) {
  const hasResult = row.findings || row.impression || row.region || row.technique;
  return (
    <div className="tp-lab-card">
      <div className="tp-lab-panel tp-lab-panel-request">
        <div className="tp-lab-panel-label">Scan requested</div>
        <div className="tp-lab-request-body">
          <div className="tp-lab-code">{row.scan_code}</div>
          <div className="tp-lab-name">
            {row.scan_name}
            {row.region && <span className="tp-med-form"> · {row.region}</span>}
          </div>
        </div>
        <div className="tp-lab-actions" onClick={(e) => e.stopPropagation()}>
          <button type="button" className="tp-icon-btn" onClick={onEdit} title="Edit report">
            <IconEdit size={14} />
          </button>
          <button type="button" className="tp-icon-btn danger" onClick={onDelete} title="Remove">
            <IconTrash size={14} />
          </button>
        </div>
      </div>

      <div className="tp-lab-panel tp-lab-panel-result">
        <div className="tp-lab-panel-label">Report</div>
        {!hasResult && (
          <div className="tp-hint">
            No report yet — press <strong>Edit</strong> to fill in, or regenerate with AI.
          </div>
        )}
        {row.technique && (
          <div className="tp-scan-line"><span className="tp-scan-lbl">Technique:</span> {row.technique}</div>
        )}
        {row.findings && (
          <div className="tp-scan-line"><span className="tp-scan-lbl">Findings:</span> {row.findings}</div>
        )}
        {row.impression && (
          <div className="tp-lab-summary">
            <span className="tp-lab-summary-label">Impression</span>
            <span>{row.impression}</span>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Scan picker modal ───────────────────────────────────────────────── */
function ScanPicker({ narrativeId, onClose, onAdded }) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(templateScans, { params: { q } });
        setRows(data.rows || []);
      } catch { notify.toast.error('Search failed'); }
      finally { setLoading(false); }
    }, 200);
    return () => clearTimeout(debounceRef.current);
  }, [q]);

  const add = async (r) => {
    setBusyId(r.id);
    try {
      await axios.post(templateNarrativeScans(narrativeId), { scanGdrgId: r.id });
      notify.toast.success(`${r.code} added — edit to fill in the report`);
      onAdded?.();
    } catch (err) {
      notify.error('Add failed', err?.response?.data?.error || err.message);
    } finally { setBusyId(null); }
  };

  return (
    <div className="tp-modal-scrim" onClick={onClose}>
      <div className="tp-modal tp-modal-wide" onClick={(e) => e.stopPropagation()}>
        <div className="tp-modal-head-bar">
          <div>
            <h3>Add a scan</h3>
            <p className="tp-modal-sub">Only imaging codes from the imported NHIS booklet.</p>
          </div>
          <button className="tp-icon-btn" onClick={onClose} title="Close">
            <IconClose size={16} />
          </button>
        </div>

        <div className="tp-editor-body" style={{ padding: '16px 24px' }}>
          <div className="tp-source-search">
            <IconSearch size={14} />
            <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search: X-ray, CT, MRI, Ultrasound, Doppler…" autoFocus />
          </div>

          <div className="tp-source-list tp-source-list-tall" style={{ marginTop: 12 }}>
            {loading && <div className="tp-source-empty">Searching…</div>}
            {!loading && rows.length === 0 && (
              <div className="tp-source-empty">
                No matches. {q ? 'Try a broader term.' : 'Type to search.'}
              </div>
            )}
            {!loading && rows.map((r) => (
              <button
                key={r.id}
                type="button"
                className="tp-source-row"
                disabled={busyId === r.id}
                onClick={() => add(r)}
              >
                <span className="tp-source-code">{r.code}</span>
                <span className="tp-source-name">{r.name}</span>
                <span className="tp-source-tags" />
                <span className="tp-source-choose">Add</span>
              </button>
            ))}
          </div>
        </div>

        <div className="tp-modal-actions">
          <button type="button" className="tp-btn tp-btn-ghost" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}

/* ── Edit scan region / technique / findings / impression ─────────── */
function ScanEditor({ narrativeId, row, onClose, onSaved }) {
  const [form, setForm] = useState({
    region:     row.region || '',
    technique:  row.technique || '',
    findings:   row.findings || '',
    impression: row.impression || '',
  });
  const [saving, setSaving] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(templateNarrativeScanRow(narrativeId, row.id), form);
      notify.toast.success('Report updated');
      onSaved?.();
    } catch (err) {
      notify.error('Save failed', err?.response?.data?.error || err.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="tp-modal-scrim" onClick={onClose}>
      <div className="tp-modal tp-modal-wide" onClick={(e) => e.stopPropagation()}>
        <div className="tp-modal-head-bar">
          <div>
            <h3>Edit scan report</h3>
            <p className="tp-modal-sub">{row.scan_code} · {row.scan_name}</p>
          </div>
          <button className="tp-icon-btn" onClick={onClose} title="Close">
            <IconClose size={16} />
          </button>
        </div>

        <form onSubmit={save} className="tp-editor-body" style={{ padding: '16px 24px' }}>
          <div className="tp-field-row">
            <div className="tp-field">
              <label>Region</label>
              <input className="tp-input" value={form.region}
                onChange={(e) => update('region', e.target.value)}
                placeholder="Chest · Abdomen · Head · Pelvis · Right Knee" />
            </div>
            <div className="tp-field">
              <label>Technique</label>
              <input className="tp-input" value={form.technique}
                onChange={(e) => update('technique', e.target.value)}
                placeholder="Supine AP view · Grey-scale + Doppler · Non-contrast" />
            </div>
          </div>
          <div className="tp-field">
            <label>Findings</label>
            <textarea className="tp-input" rows={4} value={form.findings}
              onChange={(e) => update('findings', e.target.value)}
              placeholder="Descriptive findings — what the radiologist sees" />
          </div>
          <div className="tp-field">
            <label>Impression</label>
            <textarea className="tp-input" rows={2} value={form.impression}
              onChange={(e) => update('impression', e.target.value)}
              placeholder="One-line radiological impression" />
          </div>

          <div className="tp-modal-actions">
            <button type="button" className="tp-btn tp-btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="tp-btn tp-btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save report'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
